import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ContentChild,
  ElementRef,
  EventEmitter,
  HostBinding,
  inject,
  InjectionToken,
  Input,
  NgZone,
  OnInit,
  Output,
  Renderer2,
  ViewEncapsulation,
  ChangeDetectionStrategy,
} from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { FormDialogData } from '../../../table/form-dialog.model';
import { ComponentType } from '@angular/cdk/portal';
import { fromEvent, merge, Observable, of, Subject, timer } from 'rxjs';
import { FORM_COMPONENT_TOKEN, FormComponent } from '../form.component';
import { NotificationService } from '../../../error/notification.service';
import { FormMode } from '../form-mode.enum';
import { ActivatedRoute, Router } from '@angular/router';
import { MatStepper } from '@angular/material/stepper';
import { FormStepperComponent } from '../form-stepper.component';
import { finalize, startWith, switchMap, takeUntil, tap, auditTime } from 'rxjs/operators';
import { DestroyService } from '../../../shared/destroy.service';
import Utils from '../../../utils/utils';
import { SHORT_LOCALE_ID } from '../../../shared/initialize.provider';
import { formNamesFeatures, NameFeatures } from '../form-name';
import { ConfirmDialogModel } from '../../dialog-yes-no/dialog-yes-no.model';
import { DialogYesNoService } from '../../dialog-yes-no/dialog-yes-no.service';
import { CdkStep } from '@angular/cdk/stepper';
import { AbstractControl } from '@angular/forms';
import { CvaTouchedService } from '../../../model/cva-touched.service';

export const FORM_LAYOUT_TOKEN = new InjectionToken<FormComponent<any>>('FORM_LAYOUT_TOKEN');

function isFormStepperComponent<T>(
  form: FormComponent<T> | FormStepperComponent<T>
): form is FormStepperComponent<T> {
  return 'stepper' in form;
}

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  template: '',
})
export abstract class FormLayoutComponent<T> implements OnInit, AfterViewInit {
  @Input() pageTitle = '';
  @Input() pageMode: FormMode = FormMode.ADD;
  @Input() submitButtonText: string;
  @Input() compareWith = (current: T, init: Partial<T>) =>
    JSON.stringify(current) === JSON.stringify(init);
  @Output() formInitialized = new EventEmitter<void>();
  @Output() completed = new EventEmitter<T>();

  isDialog = false;
  public isSubmitting = false;
  hasStepper = false;
  entityIsNotFound = false;
  id: number;
  cancelTitle: string;
  cancelMessage: string;
  initialFormValue: Partial<T>;
  isLoading = true;

  @ContentChild(FORM_COMPONENT_TOKEN, { static: true }) formComp:
    FormComponent<T> | FormStepperComponent<T>;
  @ContentChild(FORM_COMPONENT_TOKEN, { read: ElementRef }) formCompInputRef: ElementRef;

  notificationService = inject(NotificationService);
  destroy = inject(DestroyService);
  cd = inject(ChangeDetectorRef);
  confirmDialog = inject(DialogYesNoService);
  zone = inject(NgZone);
  locale = inject(SHORT_LOCALE_ID);
  element = inject(ElementRef);
  renderer = inject(Renderer2);
  readonly cvaTouchedService = inject(CvaTouchedService);

  public stepper: MatStepper;
  public changedStep$ = new Subject<CdkStep>();

  abstract handleSubmit(): void;

  abstract onCanceled(): void;

  ngOnInit(): void {
    this.setPageLabels();

    if (isFormStepperComponent(this.formComp)) {
      this.stepper = (this.formComp as FormStepperComponent<any>).stepper;
      this.stepper.animationDuration = '0s';
      this.hasStepper = true;
    }

    if (this.readonly) {
      this.formComp.form.markAllAsDisabled(true);
    }

    if (this.stepper) {
      this.stepper.animationDone.pipe(takeUntil(this.destroy)).subscribe(() => {
        this.changedStep$.next(this.stepper.selected);
      });
      this.stepper.steps.changes
        .pipe(
          startWith(null),
          switchMap(() => {
            const steps = this.stepper.steps.toArray().map(step => step.interactedStream);
            this.cd.detectChanges();
            return merge(...steps);
          }),
          takeUntil(this.destroy)
        )
        .subscribe(step => {
          if (step.stepControl instanceof AbstractControl) {
            this.cvaTouchedService.markAllAsTouched(step.stepControl);
          }
        });
    }

    this.initForm();
  }

  ngAfterViewInit(): void {
    if (!this.isEditMode) {
      setTimeout(() => {
        const focusableSelectors = [
          'input:not([disabled])',
          'select:not([disabled])',
          'textarea:not([disabled])',
        ];
        const focusableInput = this.formCompInputRef.nativeElement.querySelector(
          focusableSelectors.join(', ')
        );
        focusableInput?.focus();
      });
    }

    this.applyShadow();
  }

  applyShadow() {
    const selector = this.hasStepper ? '.mat-horizontal-content-container' : '.no-stepper';
    const container = this.element.nativeElement.querySelector(selector);
    if (!container) return;

    merge(fromEvent(container, 'scroll'), this.changedStep$)
      .pipe(startWith(container), auditTime(0), takeUntil(this.destroy))
      .subscribe(() => {
        const { scrollTop, scrollHeight, clientHeight } = container;
        const canScroll = scrollHeight > clientHeight;

        // Show/hide based on scroll range
        container.classList.toggle('has-scroll', canScroll);
        container.classList.toggle('at-top', scrollTop === 0);
        container.classList.toggle('at-bottom', scrollTop + clientHeight >= scrollHeight);
      });
  }

  get isEditMode(): boolean {
    return this.pageMode === FormMode.EDIT;
  }

  get isDuplicate(): boolean {
    return this.pageMode === FormMode.DUPLICATE;
  }

  get readonly(): boolean {
    return this.pageMode === FormMode.VIEW;
  }

  get formNameFeatures(): NameFeatures {
    return formNamesFeatures[this.formComp.formName];
  }

  setPageLabels(): void {
    let article = Utils.getArticle(this.formNameFeatures, this.locale, true);
    let action: string;
    if (this.readonly) {
      action = $localize`View `;
    } else if (this.isDuplicate) {
      action = $localize`Duplicate `;
      this.cancelTitle = $localize`Cancel duplication`;
      this.cancelMessage = $localize`Are you sure you want to cancel duplication?`;
    } else if (!this.isEditMode) {
      action = $localize`Create `;
      article = Utils.getArticle(this.formNameFeatures, this.locale, false);
      this.cancelTitle = $localize`Cancel creation`;
      this.cancelMessage = $localize`Are you sure you want to cancel creation?`;
    } else if (this.isEditMode) {
      action = $localize`Edit `;
      this.cancelTitle = $localize`Cancel edition`;
      this.cancelMessage = $localize`Are you sure you want to cancel edition?`;
    }
    this.pageTitle = this.pageTitle || action + article + this.formNameFeatures?.translation;
  }

  cancel(): void {
    // No baseline yet means the form is still initialising, so the user cannot have changed
    // anything — without this, cancelling early prompts to discard changes that do not exist.
    const hasChanged =
      !!this.initialFormValue &&
      !this.compareWith(this.formComp.form.getRawValue(), this.initialFormValue);
    if (hasChanged) {
      const dialogData = new ConfirmDialogModel(
        this.cancelTitle,
        this.cancelMessage,
        $localize`Discard changes`,
        $localize`Continue editing`
      );
      this.confirmDialog.open(dialogData).subscribe(() => {
        this.onCanceled();
      });
    } else {
      this.onCanceled();
    }
  }

  initForm(): void {
    this.isLoading = true;

    this.formComp.fetchDetail
      .pipe(
        tap((entity: T) => {
          this.entityIsNotFound = !entity;
          if (entity) {
            this.formComp.patchFormWithEntity(entity);
          }
        }),
        switchMap(() => timer(500)),
        finalize(() => (this.isLoading = false)),
        takeUntil(this.destroy)
      )
      .subscribe(() => {
        this.initialFormValue = this.formComp.form.getRawValue();
        this.formComp.form.markAsPristine();
        this.formInitialized.emit();
      });
  }
}

@Component({
  selector: 'core-form-dialog-layout',
  templateUrl: './form-layout.component.html',
  styleUrl: './form-layout.component.scss',
  providers: [
    {
      provide: FORM_LAYOUT_TOKEN,
      useExisting: FormDialogLayoutComponent,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FormDialogLayoutComponent<T> extends FormLayoutComponent<T> implements OnInit {
  @HostBinding('class.form-dialog-layout') someClass = true;
  override isDialog = true;
  public matDialogData = inject<FormDialogData<T>>(MAT_DIALOG_DATA);
  public dialogRef = inject(MatDialogRef<ComponentType<any>>, { optional: true });
  @Input() submitCallBack = () => {
    this.dialogRef.close(this.formComp.formToEntity(this.formComp.form));
  };

  onCanceled(): void {
    this.dialogRef.close(undefined);
  }

  @Input() handleSubmit = (): void => {
    this.cvaTouchedService.markAllAsTouched(this.formComp.form);
    if (this.readonly) {
      this.dialogRef.close(null);
      return;
    }
    if (this.formComp.form.valid) {
      this.submitCallBack();
    }
  };

  override ngOnInit(): void {
    if (this.matDialogData?.data) {
      this.formComp.fetchDetail = of(this.matDialogData.data);
      this.pageMode = this.matDialogData.mode || FormMode.ADD;
    }
    super.ngOnInit();
  }
}

@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: true,
})
export abstract class FormLayoutServiceComponent<T> extends FormLayoutComponent<T> {
  abstract ended(entity: T): void;

  @Input() handleSubmit = () => {
    this.cvaTouchedService.markAllAsTouched(this.formComp.form);
    if (this.formComp.form.valid) {
      this.isSubmitting = true;
      let entity = this.formComp.form.value as T;
      if (this.formComp?.formToEntity) {
        entity = this.formComp.formToEntity(this.formComp.form);
      }
      const serviceAction = this.isEditMode
        ? this.formComp.service.update(entity)
        : this.formComp.service.create(entity);

      const capitalizeFormName = this.formNameFeatures.translation.capitalize();
      const successMessage = this.isEditMode
        ? $localize`${capitalizeFormName} successfully updated.`
        : $localize`${capitalizeFormName} successfully created.`;

      serviceAction.pipe(finalize(() => (this.isSubmitting = false))).subscribe({
        next: (entity: T) => {
          this.notificationService.showSuccess(successMessage);
          this.ended(entity);
          this.completed.next(entity);
        },
      });
    }
  };
}

@Component({
  selector: 'core-form-dialog-service-layout',
  templateUrl: './form-layout.component.html',
  styleUrl: './form-layout.component.scss',
  providers: [
    {
      provide: FORM_LAYOUT_TOKEN,
      useExisting: FormDialogServiceLayoutComponent,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FormDialogServiceLayoutComponent<T>
  extends FormLayoutServiceComponent<T>
  implements OnInit
{
  @HostBinding('class.form-dialog-service-layout') someClass = true;

  public matDialogData = inject<FormDialogData<T>>(MAT_DIALOG_DATA);
  public dialogRef = inject(MatDialogRef, { optional: true });
  override isDialog = true;

  ended(entity: T): void {
    this.dialogRef.close(entity);
  }

  onCanceled(): void {
    this.dialogRef.close(undefined);
  }

  override ngOnInit(): void {
    if (this.matDialogData?.id) {
      const id = this.matDialogData.id as number;
      this.pageMode = this.matDialogData.mode || FormMode.EDIT;
      this.formComp.fetchDetail = this.formComp.service.getById(id, this.formComp.fields.join(','));
    } else if (this.matDialogData?.data) {
      this.formComp.fetchDetail = of(this.matDialogData.data);
      this.pageMode = this.matDialogData.mode;
    }
    super.ngOnInit();
  }
}

@Component({
  selector: 'core-form-page-service-layout',
  templateUrl: './form-layout.component.html',
  styleUrl: './form-layout.component.scss',
  providers: [
    {
      provide: FORM_LAYOUT_TOKEN,
      useExisting: FormPageServiceLayoutComponent,
    },
  ],
  encapsulation: ViewEncapsulation.None,
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class FormPageServiceLayoutComponent<T>
  extends FormLayoutServiceComponent<T>
  implements OnInit
{
  @HostBinding('class.form-page-service-layout') someClass = true;

  public router: Router = inject(Router);
  public activatedRoute: ActivatedRoute = inject(ActivatedRoute);

  @Input() fetchDetail$: Observable<T>;

  onCanceled(): void {
    const regex = new RegExp(
      `\\/(${FormMode.ADD}|(${FormMode.DUPLICATE}|${FormMode.EDIT})\\/\\d+)($|\\?.*)`
    );
    const url = this.router.url.replace(regex, '');
    this.router.navigate([url], { relativeTo: this.activatedRoute });
  }

  ended(_: T): void {
    this.onCanceled();
  }

  override ngOnInit(): void {
    this.pageMode = this.activatedRoute.snapshot.data.mode as FormMode;
    this.id = this.activatedRoute.snapshot.params['id'] as number;
    if (this.fetchDetail$) {
      this.formComp.fetchDetail = this.fetchDetail$;
    } else {
      if (this.id) {
        this.formComp.fetchDetail = this.formComp.service.getById(
          this.id,
          this.formComp.fields.join(',')
        );
      }
    }
    super.ngOnInit();
  }
}
